import {
	DragDropContext,
	Droppable,
	Draggable,
	DropResult,
	DroppableProvided,
	DraggableProvided,
	DraggableStateSnapshot
} from 'react-beautiful-dnd';
import { Icon } from '../icon/Icon';
import { useUniqueId } from '../utils/useUniqueId';
import { Button } from '../button/Button';
import { Tooltip } from '../tooltip/Tooltip';
import {
	ListWrapper,
	ListItem,
	DragHandle,
	Content,
	Feature,
	MainText,
	IconWrapper,
	TitleRow,
	TitleText,
} from './DragAndDropList.styled';
import { IconName } from '../icon/IconName';
import { ButtonVariant } from '../button/ButtonVariant';
import { ButtonSize } from '../button/ButtonSize';

interface BaseItem {
	id: string;
	featureText?: string;
	mainText: string;
	onButtonPress?: (id: string) => void;
	actionButtonLabel?: string;
	buttonVariant?: ButtonVariant;
	buttonSize?: ButtonSize;
}

export interface DragAndDropItemWithIcon extends BaseItem {
	icon?: IconName;
	iconTooltip?: string;
}

export interface DragAndDropItemWithoutIcon extends BaseItem {
	icon?: never;
}

export type DragAndDropItem = DragAndDropItemWithIcon | DragAndDropItemWithoutIcon;

interface CommonProps {
	onReorder?: (dragIndex: number, hoverIndex: number) => void;
	className?: string;
	listId?: string;
	title?: string;
	titleIcon?: IconName;
	titleActionLabel?: string;
	titleActionButtonVariant?: ButtonVariant;
	onTitleActionPress?: () => void;
}

export type DragAndDropListProps =
	| (CommonProps & {
		isDragEnabled?: true | undefined;
		items: DragAndDropItemWithoutIcon[];
	})
	| (CommonProps & {
		isDragEnabled: false;
		items: DragAndDropItemWithIcon[];
	});            

export const DragAndDropList = ({
	items,
	onReorder,
	className,
	listId,
	isDragEnabled = true,
	title,
	titleIcon,
	titleActionLabel,
	titleActionButtonVariant,
	onTitleActionPress,
}: DragAndDropListProps) => {
	const generatedId = useUniqueId('drag-and-drop-list-');
	const droppableId = listId ?? generatedId;

	const handleDragEnd = (result: DropResult) => {
		if (!result.destination) {
			return;
		}

		const sourceIndex = result.source.index;
		const destinationIndex = result.destination.index;

		if (sourceIndex === destinationIndex) {
			return;
		}

		onReorder?.(sourceIndex, destinationIndex);
	};

	return (
		<DragDropContext onDragEnd={handleDragEnd}>
			<Droppable droppableId={droppableId}>
				{(provided: DroppableProvided) => (
					<ListWrapper
						ref={provided.innerRef}
						{...provided.droppableProps}
						className={className}
					>
						{title && (
							<TitleRow>
								{titleIcon && (
									<IconWrapper>
										<Icon icon={titleIcon} />
									</IconWrapper>
								)}
								<Content>
									<TitleText>{title}</TitleText>
								</Content>
								{titleActionLabel && (
									<Button
										variant={titleActionButtonVariant || 'link'}
										size="small"
										label={titleActionLabel}
										onClick={onTitleActionPress}
									/>
								)}
							</TitleRow>
						)}
						{items.map((item, index) => (
							<Draggable
								key={item.id}
								draggableId={item.id}
								index={index}
								isDragDisabled={!isDragEnabled}
							>
								{(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
									<ListItem
										ref={provided.innerRef}
										{...provided.draggableProps}
										$isDragging={snapshot.isDragging}
										key={`${item.id}-list-item`}
									>
										{isDragEnabled ? (
											<DragHandle {...provided.dragHandleProps} $isDragging={snapshot.isDragging}>
												<Icon icon="grip-vertical" />
											</DragHandle>
										) : (item.icon && (
											<Tooltip key={`${item.id}-icon-tooltip`} message={item.iconTooltip}>
												<IconWrapper>
													<Icon icon={item.icon} />
												</IconWrapper>
											</Tooltip>
										))}
										<Content>
											{item.featureText && (
												<Feature>
													{item.featureText}
												</Feature>
											)}
											<MainText>{item.mainText}</MainText>
										</Content>
										{item.actionButtonLabel && (
											<Button
												variant={item.buttonVariant || 'link'}
												size={item.buttonSize || 'small'}
												label={item.actionButtonLabel}
												onClick={() => item.onButtonPress?.(item.id)}
											/>
										)}
									</ListItem>
								)}
							</Draggable>
						))}
						{provided.placeholder}
					</ListWrapper>
				)}
			</Droppable>
		</DragDropContext>
	);
};

